import React, { useEffect, useState } from 'react';
import { useI18n } from '../i18n/I18nProvider';


export const isAssignmentProgressFinished = (progress, expectedPhase) => (
  (
    expectedPhase === 'validation'
    && progress?.phase === 'validation'
    && progress?.state === 'ready'
  )
  || (
    expectedPhase === 'generation'
    && progress?.phase === 'generation'
    && ['foreground_complete', 'materialized', 'failed'].includes(progress?.state)
  )
);

export const generationProgressOnly = (progress) => (
  progress?.phase === 'generation' ? progress : null
);

export const waitForAssignmentMaterialization = async ({
  classId,
  preparationId,
  token,
  fetchImpl = fetch,
  intervalMs = 800,
  timeoutMs = 30 * 60 * 1000,
}) => {
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    const response = await fetchImpl(
      `/api/classes/${classId}/assignment-preparations/${preparationId}/progress`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    const progress = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(progress?.message || 'Unable to check assignment progress.');
    }
    if (
      progress?.phase === 'generation'
      && ['foreground_complete', 'materialized'].includes(progress?.state)
    ) {
      return progress;
    }
    if (progress?.phase === 'generation' && progress?.state === 'failed') {
      throw new Error(
        progress?.message
        || 'Practice activities could not be completed. Nothing was assigned; please try again.',
      );
    }
    await new Promise(resolve => setTimeout(resolve, intervalMs));
  }
  throw new Error('Preparing the practice activities is taking longer than expected. Please try again.');
};


export const useAssignmentGenerationProgress = ({
  active,
  classId,
  preparationId,
  expectedPhase = 'generation',
}) => {
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    if (!active || !classId || !preparationId) {
      setProgress(null);
      return undefined;
    }
    // A preparation ID is a new job. Never let the previous job's terminal
    // state make the new one appear failed before its first poll completes.
    setProgress(null);
    let cancelled = false;
    let timer = null;
    const token = localStorage.getItem('token');

    const poll = async () => {
      try {
        const response = await fetch(
          `/api/classes/${classId}/assignment-preparations/${preparationId}/progress`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (response.ok && !cancelled) {
          const next = await response.json();
          if (cancelled) return;
          setProgress(next);
          const expectedFinished = isAssignmentProgressFinished(
            next,
            expectedPhase,
          );
          if (!expectedFinished) {
            timer = window.setTimeout(poll, 650);
          }
          return;
        }
      } catch (_) {
        // The assignment request remains authoritative; a missed poll is harmless.
      }
      if (!cancelled) timer = window.setTimeout(poll, 1000);
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [active, classId, preparationId, expectedPhase]);

  return progress;
};


const TIER_LABELS = {
  tier_6: 'Starter',
  tier_5: 'Foundation',
  tier_4: 'Bridge',
  tier_3: 'Core',
  tier_2: 'Advanced',
  tier_1: 'Challenge',
};

export const generationProgressDetail = (progress) => {
  const total = Number(progress?.total || 0);
  const completed = Math.min(Number(progress?.completed || 0), total || 0);
  const message = String(progress?.message || '').trim();
  if (message) return message;
  const tier = TIER_LABELS[progress?.current_tier] || '';
  return total > 0
    ? `${completed} of ${total} activities complete${progress?.current_word ? ` · ${progress.current_word}${tier ? ` · ${tier}` : ''}` : ''}`
    : 'Preparing practice activities...';
};

const VALIDATION_STAGES = {
  parsing: ['Reading your list', 'Finding each word and its intended meaning.'],
  inspecting: ['Checking words and meanings', 'Up to ten entries are checked at the same time.'],
  matching: ['Finding available meanings', 'Reusing matching meanings that are already ready for students.'],
  verifying: ['Double-checking new meanings', 'Keeping genuinely different meanings separate.'],
  ready: ['Ready to review', 'Check the meanings below before assigning them.'],
  failed: ['A few entries need attention', 'Try the unfinished entries again without restarting the whole list.'],
};


const AssignmentGenerationProgress = ({ active, progress, phase = 'generation' }) => {
  const { t } = useI18n();
  if (!active) return null;
  if (phase === 'validation') {
    const stage = VALIDATION_STAGES[progress?.state] || VALIDATION_STAGES.parsing;
    const totalSteps = Number(progress?.total || 0);
    const completedSteps = Math.min(Number(progress?.completed || 0), totalSteps);
    const validationPercent = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;
    const currentWord = String(progress?.current_word || '').trim();
    return (
      <div className="border-t border-blue-200 bg-blue-50 px-4 py-3 sm:px-6" role="status" aria-live="polite">
        <div className="flex items-center justify-between gap-3 text-sm">
          <span className="font-semibold text-blue-950">{stage[0]}</span>
          <span className="tabular-nums text-blue-800">
            {totalSteps > 0 ? `${completedSteps}/${totalSteps} · ${validationPercent}%` : 'Preparing'}
          </span>
        </div>
        <div className="mt-2 h-2.5 overflow-hidden bg-blue-100" role="progressbar" aria-label="Meaning review progress" aria-valuemin="0" aria-valuemax={totalSteps || 1} aria-valuenow={completedSteps}>
          <div
            className={`h-full bg-blue-600 transition-[width] duration-300 ${totalSteps === 0 ? 'w-1/6 animate-pulse' : ''}`}
            style={totalSteps > 0 ? { width: `${validationPercent}%` } : undefined}
          />
        </div>
        <p className="mt-2 text-xs text-blue-900">
          {stage[1]}{currentWord ? ` Current: ${currentWord}` : ''}
        </p>
      </div>
    );
  }
  const generationProgress = generationProgressOnly(progress);
  const total = Number(generationProgress?.total || 0);
  const completed = Math.min(Number(generationProgress?.completed || 0), total || 0);
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  const assignedTotal = Number(generationProgress?.assigned_total || 0);
  const assignedCompleted = Math.min(
    Number(generationProgress?.assigned_completed || 0),
    assignedTotal || 0,
  );
  const assignedPercent = assignedTotal > 0
    ? Math.round((assignedCompleted / assignedTotal) * 100)
    : 0;
  const failedCount = Number(generationProgress?.failed_count || 0);
  const tier = TIER_LABELS[generationProgress?.current_tier] || '';
  const fallbackDetail = total > 0
    ? `${completed} of ${total} activities complete${generationProgress?.current_word ? ` · ${generationProgress.current_word}${tier ? ` · ${tier}` : ''}` : ''}`
    : 'Preparing practice activities...';
  const detail = generationProgress?.message
    ? generationProgressDetail(generationProgress)
    : fallbackDetail;

  return (
    <div className="border-t border-blue-200 bg-blue-50 px-4 py-3 sm:px-6" role="status" aria-live="polite">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="font-semibold text-emerald-950">{t('Available to students')}</span>
        <span className="tabular-nums text-emerald-800">
          {assignedTotal > 0 ? `${assignedCompleted}/${assignedTotal} · ${assignedPercent}%` : 'Starting'}
        </span>
      </div>
      <div
        className="mt-2 h-2.5 overflow-hidden bg-emerald-100"
        role="progressbar"
        aria-label={t('Meanings available to students')}
        aria-valuemin="0"
        aria-valuemax={assignedTotal || 1}
        aria-valuenow={assignedCompleted}
      >
        <div
          className={`h-full bg-emerald-600 transition-[width] duration-300 ${assignedTotal === 0 ? 'w-1/6 animate-pulse' : ''}`}
          style={assignedTotal > 0 ? { width: `${assignedPercent}%` } : undefined}
        />
      </div>
      <p className="mt-2 text-xs text-emerald-900">
        {t("Each meaning appears in the student's task as soon as its essential practice is ready.")}
      </p>

      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="mt-4 font-semibold text-blue-950">{t('Preparing ready-to-learn practice')}</span>
        <span className="mt-4 tabular-nums text-blue-800">{total > 0 ? `${completed}/${total} · ${percent}%` : 'Starting'}</span>
      </div>
      <div
        className="mt-2 h-2.5 overflow-hidden bg-blue-100"
        role="progressbar"
        aria-label="Practice activity preparation"
        aria-valuemin="0"
        aria-valuemax={total || 1}
        aria-valuenow={completed}
      >
        <div
          className={`h-full bg-blue-600 transition-[width] duration-300 ${total === 0 ? 'w-1/6 animate-pulse' : ''}`}
          style={total > 0 ? { width: `${percent}%` } : undefined}
        />
      </div>
      <p data-i18n-skip className="mt-2 text-xs text-blue-900">{detail}</p>
      {failedCount > 0 && (
        <p className="mt-2 text-xs text-gray-700">
          {`${failedCount} unfinished ${failedCount === 1 ? 'entry' : 'entries'} can be retried later.`}
        </p>
      )}
    </div>
  );
};


export default AssignmentGenerationProgress;
